import classNames from 'classnames';
import { useCallback, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import useGameState from '../../hooks/useGameState';
import * as AppPropTypes from '../../lib/PropTypes';
import { getImageAtSize } from '../../lib/utils';

import { useThemeClassName } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';
import SparklesIcon from '../icons/Sparkles';
import Loading from '../partials/Loading';
import BaseGame, { GameApi } from './Base';

import styles from '../../../styles/games/differences.module.css';

interface DifferencesGameProps {
    game: DifferencesGame;
    participation: Participation;
    canPlay?: boolean;
    reload: () => void;
    className?: string;
}

function DifferencesGame({
    game,
    participation,
    canPlay = false,
    reload,
    className = null,
}: DifferencesGameProps) {
    const intl = useIntl();
    const themeClassName = useThemeClassName(styles);
    const { currentQuestion, currentAnswer } = useGameState(game, participation);

    const {
        id: questionId,
        originalImage,
        differencesImage,
        coordinatesCount,
    } = (currentQuestion as DifferencesQuestion) || {};

    const mediumOriginalImage = getImageAtSize(originalImage, 'medium');
    const { url: mediumOriginalUrl = null } = mediumOriginalImage || {};

    const mediumDifferenceImage = getImageAtSize(differencesImage, 'medium');
    const { url: mediumDifferenceUrl = null } = mediumDifferenceImage || {};

    const { value: correctGuesses } = currentAnswer || {};
    const rightAnswersCount = (correctGuesses || []).length;
    const allValidAnswers = coordinatesCount === rightAnswersCount;

    const [hintUsed, setHintUsed] = useState(false);
    const [loading, setLoading] = useState(false);
    const apiRef = useRef<GameApi>(null);

    const onImagePointerDown = useCallback(
        (e: React.PointerEvent<HTMLImageElement>) => {
            if (loading) {
                return;
            }
            const { answerQuestion } = apiRef.current!;
            const { offsetHeight: imgHeight = 0, offsetWidth: imgWidth = 0 } = e.currentTarget;
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            answerQuestion(questionId, { type: 'click', x: x / imgWidth, y: y / imgHeight });
        },
        [loading, questionId],
    );

    const onClickHint = useCallback(() => {
        const { answerQuestion } = apiRef.current!;
        answerQuestion(questionId, { type: 'hint' });
        setHintUsed(true);
    }, [questionId]);

    return (
        <BaseGame
            game={game}
            participation={participation}
            reload={reload}
            isValid={allValidAnswers}
            apiRef={apiRef}
            canPlay={canPlay}
            withTimer
            onLoadingChange={setLoading}
            className={classNames([styles.container, themeClassName, className])}
        >
            <div className={styles.game}>
                <div className={classNames([styles.cardContainer, styles.original])}>
                    <div className={styles.label}>
                        <FormattedMessage
                            defaultMessage="Reference"
                            description="Differences image label"
                        />
                    </div>
                    <img
                        src={mediumOriginalUrl}
                        alt={intl.formatMessage({
                            defaultMessage: 'Reference',
                            description: 'Differences image label',
                        })}
                        className={styles.gameImage}
                    />
                </div>
                <div className={classNames([styles.cardContainer, styles.difference])}>
                    <div className={styles.label}>
                        <FormattedMessage
                            defaultMessage="Play here"
                            description="Differences image label"
                        />
                    </div>
                    <img
                        src={mediumDifferenceUrl}
                        alt={intl.formatMessage({
                            defaultMessage: 'Play here',
                            description: 'Differences image label',
                        })}
                        className={styles.gameImage}
                        onPointerDown={onImagePointerDown}
                    />
                    {(correctGuesses || []).map(({ x = null, y = null, id = null }) =>
                        x !== null && y !== null ? (
                            <div
                                className={classNames([styles.question])}
                                key={`differences-question-${id}`}
                                style={{
                                    top: `${y * 100}%`,
                                    left: `${x * 100}%`,
                                    pointerEvents: 'none',
                                }}
                            />
                        ) : null,
                    )}
                    {loading ? <Loading loading={loading} className={styles.loading} /> : null}
                </div>
                <div className={styles.info}>
                    <div className={styles.questionsCount}>
                        <span className={styles.currentQuestions}>{rightAnswersCount}</span>
                        {' / '}
                        <span className={styles.totalQuestions}>{coordinatesCount}</span>
                    </div>
                    <Button
                        disabled={hintUsed}
                        onClick={onClickHint}
                        className={classNames([
                            styles.hintButton,
                            {
                                [styles.disabled]: hintUsed,
                            },
                        ])}
                    >
                        <SparklesIcon className={styles.icon} />
                        <FormattedMessage defaultMessage="Hint" description="Button label" />
                    </Button>
                </div>
            </div>
        </BaseGame>
    );
}

export default DifferencesGame;
